import { z } from 'zod';
import { lotTypeSchema, ILotType } from './lotType';

/**
 * Schema for validating lot type form data.
 * Extends the base lot type schema with form-specific validations.
 *
 * @property {string} name - Lot type name (max 255 characters)
 * @property {boolean} isSwappable - Whether lots of this type can be swapped
 */
export const lotTypeFormSchema = lotTypeSchema.extend({});

/**
 * Default form schema for new lot types.
 * Makes all required fields optional with empty defaults.
 */
export const lotTypeDefaultFormSchema = lotTypeFormSchema.extend({
    name: z.string().max(255, 'Name can only be up to 255 characters long.').optional().default(''),
});

/**
 * TypeScript interface representing LotType form values.
 * Generated from the Zod schema for type safety.
 */
export type ILotTypeFormValues = z.infer<typeof lotTypeFormSchema>;

/**
 * Factory function to create form values for a lot type.
 * Handles both new and existing lot types.
 *
 * @param {ILotType} [lotType] - Optional existing lot type to populate form
 * @returns {ILotTypeFormValues} Form values with appropriate defaults
 */
export const LotTypeFormValues = (lotType ?: ILotType) : ILotTypeFormValues => {
    // Handle the default case (new lot type)
    if (!lotType) {
        return lotTypeDefaultFormSchema.parse({});
    }

    // Transform the ILotType data into ILotTypeFormValues structure
    return lotTypeFormSchema.parse({
        ...lotType,
    });
};

/**
 * Validates lot type form values.
 * Logs any validation errors to the console.
 *
 * @param {ILotTypeFormValues} formValues - The form values to validate
 * @returns {boolean} True if validation passes, false otherwise
 */
export const validateLotTypeForm = (formValues : ILotTypeFormValues) : boolean => {
    try {
        lotTypeFormSchema.parse(formValues);
        return true;
    } catch (error) {
        if (error instanceof z.ZodError) {
            // eslint-disable-next-line no-console
            console.error('Lot type form validation failed:', error.errors);
        } else {
            // eslint-disable-next-line no-console
            console.error('An unexpected error occurred during validation:', error);
        }
        return false;
    }
};
